import { useGetApplicationEditConfigurationQuery } from '../../generated/graphql';

/**
 * Whether the quote page's "Edit" control is available to this viewer, from
 * the two Configuration flags behind it, read together by one request
 * (`getApplicationEditConfiguration`):
 *
 * - enableApplicationEdit (NCP-5437): whether Edit exists in this environment
 *   at all. Absent, in flight or failed mean off — the resolver's `?? false`.
 * - restrictApplicationEditToInternal (NCP-5642): internal (Foxquilt) brokers
 *   only (true) or everyone (false). Absent, in flight or failed mean
 *   restricted — the resolver's `?? true` — so Edit never widens before the
 *   flags settle.
 *
 * Shared by the policy card that renders the button and the Back popup whose
 * copy names it, so the two can never disagree. The journey gate (New
 * Business, NCP-5396) stays with the caller.
 */
export const useCanViewerEditApplication = (
  isInternalUser: boolean
): boolean => {
  const { data } = useGetApplicationEditConfigurationQuery({
    // Global and effectively static for the life of a page load; every policy
    // card, the popup and any remount read it straight from the cache, so it
    // still costs a single request.
    fetchPolicy: 'cache-first',
    // A failed flag read must not surface as an unhandled Apollo error on a
    // page that renders perfectly well without it.
    onError: () => undefined
  });

  const configuration = data?.getApplicationEditConfiguration;
  const isEnabled = configuration?.enableApplicationEdit ?? false;
  const isRestrictedToInternal =
    configuration?.restrictApplicationEditToInternal ?? true;

  return isEnabled && (!isRestrictedToInternal || isInternalUser);
};
